import { useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { ManageLimitsShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { todayIso } from "../../../components/portfolio-risk-concentration/ScenarioAsOfPicker";
import { StepsNavigation } from "../../../components/common/StepsNavigation";
import {
  type ScenarioCondition,
  type ScenarioContract,
  type ScenarioWaterfallResult,
  useResetScenarioAnswers,
} from "../../../utils/portfolio-risk-concentration/api/scenario";
import type { ValueBasis } from "../../../utils/portfolio-risk-concentration/api/concentration";
import ScenarioPortfolioStep from "./scenario/ScenarioPortfolioStep";
import ScenarioApplyStep from "./scenario/ScenarioApplyStep";
import ScenarioMonitorStep from "./scenario/ScenarioMonitorStep";
import ScenarioSnapshotStep from "./scenario/ScenarioSnapshotStep";
import ScenarioProposeStep from "./scenario/ScenarioProposeStep";
import ScenarioReconciliationPage from "./ScenarioReconciliationPage";

const BASE = "/risk-concentrations/scenario";

// The wizard's own step trail — a second, embedded StepsNavigation (fixed={false}, so it sits
// in normal document flow instead of stacking a second fixed bar under the product-wide one)
// rendered once here, in the exact spot ScenarioHeader's old per-page breadcrumb used to
// occupy. Scenario Portfolio is a single flat entry in the outer product nav
// (PortfolioRiskConcentration.tsx's navItemsFor()) — these steps read as one wizard's own
// progression, not as six more pages mixed into the same flat list as Monitor's Manage
// Capital/Investors/Limits/Alerts children.
//
// Reconciliation is the 6th entry, marked alwaysEnabled: unlike the other 5 (which unlock in
// order, one CTA at a time), it isn't a sequential step — it's a status check for what's
// already been approved, and per a real user report (no path back to reconciliation status
// after approving a pledge proposal) it has to stay reachable no matter which step you're
// currently on, not gated behind finishing Propose first.
// Labels match the original breadcrumb trail's wording (ScenarioHeader's old `crumbs`:
// "Scenario Portfolio › Scenario › Scenario Monitor › Scenario Capacity › Propose"; Capacity has since given way to Scenario Snapshot), not the
// route segment names, since that's the wording this product's users already know these
// steps by.
const SCENARIO_STEPS = [
  { path: BASE, label: "Scenario Portfolio" },
  { path: `${BASE}/apply`, label: "Scenario" },
  { path: `${BASE}/monitor`, label: "Scenario Monitor" },
  { path: `${BASE}/snapshot`, label: "Scenario Snapshot" },
  { path: `${BASE}/propose`, label: "Propose" },
  { path: `${BASE}/reconciliation`, label: "Reconciliation", alwaysEnabled: true },
];

/**
 * Simulator — the 5-page what-if wizard, plus Reconciliation as an always-reachable 6th entry
 * in the same step trail (see the SCENARIO_STEPS comment above). ScenarioHeader.tsx still
 * supplies the shared title/subtitle/"SCENARIO · NOT REAL" badge convention every wizard step
 * uses; its old per-page crumbs trail is superseded by the embedded SCENARIO_STEPS nav
 * rendered once below.
 *
 * State lives here, once, for the life of one scenario — nothing is persisted until
 * Finalize/Propose writes a governed record. Closing the tab mid-scenario loses it, and
 * that's the correct behavior: a scenario is a draft, not a document.
 *
 * The embedded trail runs in state mode (currentStepIndex/onStepChange/nextBlocked=true),
 * same convention as UnderwritingEngineV2's AppLayout.tsx: a step ahead of the one you're on is
 * shown disabled (cursor-not-allowed), and progressing only happens through each step's own
 * in-page "Continue"/"Start Scenario →" button — not by clicking ahead in the trail. Stepping
 * BACK to an already-visited step via the trail is still allowed, same as Underwriting's, and
 * Reconciliation (alwaysEnabled) ignores this blocking altogether. currentStepIndex is derived
 * from location.pathname rather than kept as separate state, since real nested routes (not a
 * step-index state machine) are what drive which page renders here.
 */
export interface ScenarioDraft {
  sourceInvestorCode: string;
  dateFrom: string;
  dateTo: string;
  existingPortfolioDollars: number | null;
  contracts: ScenarioContract[];
  /** The contract schedule keys of the source pool when it started from an uploaded contract list; null for a pool picked by code and window. */
  sourceKeys: string[] | null;
  /** The scenario's effective date (yyyy-MM-dd), today or earlier: Scenario Monitor and Snapshot read the portfolio as of it. Both steps
   *  share it, so a date chosen on one is the date on the other. */
  asOfDate: string;
  /** The data file date the pool was ACTUALLY read on (the latest file on or before the effective date asked for), set when the scenario
   *  starts. It is what Propose saves, so reopening the scenario reads the same day even though `asOfDate` can be moved afterwards. */
  poolFileDate: string | null;
  conditions: ScenarioCondition[];
  /** The approved scenario this draft was opened from (its label, as on the Reconciliation page), or null for a new scenario. */
  approvedScenarioLabel: string | null;
  waterfall: ScenarioWaterfallResult | null;
  /** The filters last chosen on Scenario Monitor / Scenario Snapshot, so leaving a step and coming back shows them as they were left. */
  monitorView: { investor: string; facility: string; product: string; measuredIn: ValueBasis } | null;
  snapshotView: { facility: string; measuredIn: ValueBasis } | null;
}

const emptyDraft = (): ScenarioDraft => ({
  sourceInvestorCode: "",
  dateFrom: "",
  dateTo: "",
  existingPortfolioDollars: null,
  contracts: [],
  sourceKeys: null,
  asOfDate: todayIso(),
  poolFileDate: null,
  conditions: [],
  approvedScenarioLabel: null,
  waterfall: null,
  monitorView: null,
  snapshotView: null,
});

function currentScenarioStepIndex(pathname: string): number {
  const index = SCENARIO_STEPS.findIndex((step) =>
    step.path === BASE
      ? pathname === BASE || pathname === `${BASE}/`
      : pathname === step.path || pathname.startsWith(`${step.path}/`)
  );
  return index >= 0 ? index : 0;
}

export default function ScenarioSimulatorPage() {
  const [draft, setDraft] = useState<ScenarioDraft>(emptyDraft);
  const navigate = useNavigate();
  const location = useLocation();
  const currentStepIndex = currentScenarioStepIndex(location.pathname);

  function patch(partial: Partial<ScenarioDraft>) {
    setDraft((d) => ({ ...d, ...partial }));
  }

  const resetAnswers = useResetScenarioAnswers();

  function reset() {
    resetAnswers(); // a new scenario starts from nothing kept
    setDraft(emptyDraft());
    navigate(BASE);
  }

  return (
    <ManageLimitsShell backTo="/risk-concentrations" backLabel="← Back to Monitor">
      <StepsNavigation
        navItems={SCENARIO_STEPS}
        basePath={BASE}
        currentStepIndex={currentStepIndex}
        onStepChange={(index) => navigate(SCENARIO_STEPS[index].path)}
        nextBlocked={true}
        fixed={false}
        className="mb-4 px-0"
      />
      <Routes>
        <Route index element={<ScenarioPortfolioStep draft={draft} patch={patch} base={BASE} />} />
        <Route path="apply" element={<ScenarioApplyStep draft={draft} patch={patch} base={BASE} />} />
        <Route path="monitor" element={<ScenarioMonitorStep draft={draft} patch={patch} base={BASE} />} />
        <Route path="snapshot" element={<ScenarioSnapshotStep draft={draft} patch={patch} base={BASE} />} />
        <Route path="propose" element={<ScenarioProposeStep draft={draft} onFinalized={reset} base={BASE} />} />
        <Route path="reconciliation" element={<ScenarioReconciliationPage />} />
      </Routes>
    </ManageLimitsShell>
  );
}
